import { Component, Prop, h, Element, Method } from '@stencil/core';

declare global {
  interface Window {
    lpTag: any;
    _lptStop: any;
    attachEvent: any;
  }
}
/**
 * COMPNAME is used to render the Live Person button.
 * This is used to allow for pulling in their JavaScript
 * in an ad hoc manner, versus having every page request
 * increase their page weight with these calls
 */
@Component({
  tag: 'aafp-live-person',
  shadow: false,
})
export class AafpLivePerson {
  /**
   * The image used for the button
   */
  @Prop() buttonImage!: string;
  /**
   * The alt attribute of the button
   */
  @Prop() altText!: string;
  /**
   * ID prop to identifiy the component
   */
  @Prop() buttonId!: string;
  /**
   * The account key we send to Live Person
   */
  @Prop() siteKey!: string;

  @Element() element: HTMLElement;

  @Method()
  async showChat() {
    // Showing the status spinner
    this.element.classList.add('loading');

    let that = this;

    window.addEventListener('lpLoad', () => {
      that.toggleButtons();
    });

    // Code taken from: 
    // https://github.com/LivePersonInc/chat-js-sdk-sample-app/blob/master/index.html
    // Small changes added for scoping
    // and to call external functions/events
    window.lpTag = window.lpTag || {}, "undefined" == typeof window.lpTag._tagCount ? (window.lpTag = {
      site: this.siteKey || "",
      section: window.lpTag.section || "",
      tagletSection: window.lpTag.tagletSection || null,
      autoStart: window.lpTag.autoStart !== !1,
      ovr: window.lpTag.ovr || {},
      _v: "1.7.0",
      _tagCount: 1,
      protocol: "https:",
      events: {
        bind: function (t: any, e: any, i: any) {
          window.lpTag.defer(() => {
            window.lpTag.events.bind(t, e, i)
          }, 0)
        }, trigger: function (t: any, e: any, i: any) {
          window.lpTag.defer(() => {
            window.lpTag.events.trigger(t, e, i)
          }, 1)
        }
      },
      defer: function (t: any, e: any) {
        0 == e ? (this._defB = this._defB || [], this._defB.push(t)) : 1 == e ? (this._defT = this._defT || [], this._defT.push(t)) : (this._defL = this._defL || [], this._defL.push(t))
      },
      load: function (t: any, e: any, i: any) {
        var n = this;
        setTimeout(() => {
          n._load(t, e, i)
        }, 0)
      },
      _load: function (t: any, e: any, i: any) {
        var n = t;
        t || (n = `https://lptag.liveperson.net/tag/tag.js?site=${this.site}`);
        var a = document.createElement("script") as HTMLScriptElement;
        var head = document.head as HTMLHeadElement;
        a.setAttribute("charset", e ? e : "UTF-8"), i && a.setAttribute("id", i), a.setAttribute("src", n), head.appendChild(a)
        const tagScriptEl = that.createScriptElement(n, i, e);
        if (tagScriptEl) {
          head.appendChild(tagScriptEl);
        }

        const configSrc = `https://lptag.liveperson.net/lptag/api/account/${this.site}/configuration/applications/taglets/.jsonp?v=2.0&df=0&b=3`;
        const configScriptEl = that.createScriptElement(configSrc, `${i}-config`, e);
        if (configScriptEl) {
          head.appendChild(configScriptEl);
        }

        window.lpTag.events.bind({
          appName: 'LP_OFFERS',
          eventName: 'OFFER_DISPLAY',
          func: function() {
            // Triggering a custom event on load
            // This is to avoid scoping issues with the definition of 'this'
            window.dispatchEvent(new CustomEvent('lpLoad'));
          },
          context: this
        });

      },
      init: function () {
        this._timing = this._timing || {}, this._timing.start = (new Date).getTime();
        var t = this;
        window.attachEvent ? window.attachEvent("onload", () => {
          t._domReady("domReady")
        }) : (window.addEventListener("DOMContentLoaded", () => {
          t._domReady("contReady")
        }, !1), window.addEventListener("load", () => {
          t._domReady("domReady")
        }, !1)), "undefined" == typeof window._lptStop && this.load()
      },
      start: function () {
        this.autoStart = !0
      },
      _domReady: function (t: any) {
        this.isDom || (this.isDom = !0, this.events.trigger("LPT", "DOM_READY", {t: t})), this._timing[t] = (new Date).getTime()
      },
      vars: window.lpTag.vars || [],
      dbs: window.lpTag.dbs || [],
      ctn: window.lpTag.ctn || [],
      sdes: window.lpTag.sdes || [],
      ev: window.lpTag.ev || []
    }, window.lpTag.init()) : window.lpTag._tagCount += 1;
  }

  private toggleButtons() {
    // Hiding post-click to avoid situation where the button disappears before the click/element resolves
    const currentButton = document.querySelector<HTMLElement>('aafp-live-person');
    if (!currentButton) {
      return;
    }

    // If we've already hidden the button, then we don't need to re-hide it or re-click the LP button
    if (currentButton.classList.contains('adl-hide')) {
      return;
    }

    currentButton.classList.add('adl-hide');

    const newButton = document.querySelector<HTMLElement>('.LPMcontainer.LPMoverlay');
    if (newButton) {
      // And triggering the click event on the new event to show the chat module
      newButton.click();
    }

  }

  private createScriptElement(src: string, id: string, charset: string): HTMLScriptElement | null {
    if (!src) {
      return null;
    }

    let el = id && document.getElementById(id) as HTMLScriptElement;
    // If the element already exists, we will avoid double-calling it
    if (el) {
      return null;
    }

    el = document.createElement('script') as HTMLScriptElement;
    if (id) {
      el.id = id;
    }

    el.src = src;
    el.setAttribute('charset', charset ? charset : 'UTF-8');

    return el;
  }

  render() {
    return (
      <div id={this.buttonId} class="aafp-live-person" role="button" tabindex="0" onClick={() => this.showChat()}>
        <aafp-spinner-inline size="md"></aafp-spinner-inline>
        <img src={this.buttonImage} alt={this.altText}/>
      </div>
    );
  }
}
